import _ from 'lodash';
import { lsApiService, lsErrorService, lsImageHelpers } from 'ls-vue-common';
import { lsModuleHelpers } from '../helpers';

import {
  CUSTOMER_LEADS__GET_MESSAGES,
  CUSTOMER_LEADS__SEND_MESSAGE,
  CUSTOMER_LEADS__HIRE_PRO,
  CUSTOMER_LEADS__DECLINE_PRO,
  CUSTOMER_LEADS__INITIALIZE,
  CUSTOMER_LEADS__MUTATE_APPEND_MESSAGE,
  CUSTOMER_LEADS__MUTATE_SET_MESSAGES,
  CUSTOMER_LEADS__MUTATE_LOADING_STATUS,
  CUSTOMER_LEADS__RESET,
  CUSTOMER_LEADS__UPDATE_HIRED_DETAILS,
  CUSTOMER_LEADS__CLOSE_LEAD,
  CUSTOMER_LEADS__STILL_LOOKING,
  CUSTOMER_LEADS__REVIEW_REMIND_ME,
  CUSTOMER_LEADS__SUBMIT_REVIEW,
  FEATURE_FLAGS__REGISTER_EXPOSURE,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  PROPERTIES__MUTATE_PROPERTY,
} from '@/store/types';

const createInitialState = () => {
  return {
    messages: [],
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
  };
};

export const mutations = {
  [CUSTOMER_LEADS__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },
  [CUSTOMER_LEADS__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },
  [CUSTOMER_LEADS__MUTATE_SET_MESSAGES](state, payload) {
    state.messages = [...payload];
  },
  [CUSTOMER_LEADS__MUTATE_APPEND_MESSAGE](state, payload) {
    state.messages = [...state.messages, payload];
  },
};

export const actions = {
  async [CUSTOMER_LEADS__INITIALIZE]({ dispatch }) {
    dispatch(FEATURE_FLAGS__REGISTER_EXPOSURE, {
      flag: 'customer.customerLeads.contact.decline',
    });
  },

  async [CUSTOMER_LEADS__GET_MESSAGES](
    { commit, rootState },
    { customer_lead_id, property_id, contact_id }
  ) {
    commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    const { customer_id } = rootState.auth.user;

    try {
      const allMessages = await lsApiService.request({
        method: 'GET',
        route: `/customers/${customer_id}/properties/${property_id}/customer-leads/${customer_lead_id}/contacts/${contact_id}/messages`,
      });
      commit(
        CUSTOMER_LEADS__MUTATE_SET_MESSAGES,
        _.orderBy(allMessages.data, ['created_at'], ['asc'])
      );
    } catch (error) {
      lsErrorService.log({ error, showAlert: true });
    } finally {
      commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
        isLoading: false,
      });
    }
  },

  async [CUSTOMER_LEADS__SEND_MESSAGE](
    { commit, rootState },
    { customer_lead_id, property_id, contact_id, message, images }
  ) {
    commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    const payload = new FormData();

    if (message) {
      payload.append('message', message);
    }

    if (_.size(images) >= 1) {
      const imagesWithMeta = lsImageHelpers.generateImagesWithMetaPayload({
        images,
        tag: 'conversation.customerLeads',
        namePrefix: 'Conversation - Customer Leads',
      });

      _.forEach(imagesWithMeta, (imageAndMeta, idx) => {
        const { image, meta } = imageAndMeta;
        // need to include image obj with name, type, uri for file upload to work
        payload.append(`images[${idx}]`, image);
        payload.append(`images_meta[${idx}][tag]`, meta.tag);
        payload.append(`images_meta[${idx}][title]`, meta.title);
      });
    }

    try {
      const result = await lsApiService.request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/customer-leads/${customer_lead_id}/contacts/${contact_id}/messages`,
        payload,
      });

      commit(CUSTOMER_LEADS__MUTATE_APPEND_MESSAGE, result.data);
    } catch (error) {
      lsErrorService.log({ error, showAlert: true });
    } finally {
      commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
        isUpdating: false,
      });
    }
  },

  async [CUSTOMER_LEADS__HIRE_PRO](
    { dispatch, commit, rootState },
    { customerLeadId, propertyId, contactId, onSuccess }
  ) {
    commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    try {
      const response = await lsApiService.request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${propertyId}/customer-leads/${customerLeadId}/contacts/${contactId}/hired`,
      });

      await dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, { forceRefresh: true });

      onSuccess(response);
    } catch (error) {
      lsErrorService.log({ error, showAlert: true });
    } finally {
      commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
        isUpdating: false,
      });
    }
  },

  async [CUSTOMER_LEADS__DECLINE_PRO](
    { commit, rootState },
    { customerLeadId, propertyId, contactId, reason = null, onSuccess }
  ) {
    commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    try {
      await lsApiService.request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${propertyId}/customer-leads/${customerLeadId}/contacts/${contactId}/decline`,
        payload: {
          reason,
        },
      });

      const property = _.cloneDeep(rootState.properties.propertiesById[propertyId]);
      _.remove(
        property.customer_leads[_.findIndex(property.customer_leads, { id: customerLeadId })]
          .customer_lead_contacts,
        { id: contactId }
      );
      commit(PROPERTIES__MUTATE_PROPERTY, property);

      onSuccess();
    } catch (error) {
      lsErrorService.log({ error, showAlert: true });
    } finally {
      commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
        isUpdating: false,
      });
    }
  },

  async [CUSTOMER_LEADS__UPDATE_HIRED_DETAILS](
    { dispatch, commit, rootState },
    { customer_lead_id, property_id, contact_id, onSuccess, hiredDetails }
  ) {
    commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;
    const { reason, cost, expected_completion_date } = hiredDetails;

    try {
      await lsApiService.request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/customer-leads/${customer_lead_id}/contacts/${contact_id}/hired-details`,
        payload: {
          reason,
          cost,
          expected_completion_date,
        },
      });
      dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, { forceRefresh: true });
      onSuccess();
    } catch (error) {
      lsErrorService.log({ error, showAlert: true });
    } finally {
      commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
        isUpdating: false,
      });
    }
  },

  async [CUSTOMER_LEADS__CLOSE_LEAD](
    { dispatch, commit, rootState },
    { customerLeadId, propertyId, reason, onSuccess = _.noop }
  ) {
    commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    try {
      await lsApiService.request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${propertyId}/customer-leads/${customerLeadId}/close`,
        payload: { reason },
      });

      await dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, { forceRefresh: true });
      onSuccess();
    } catch (error) {
      lsErrorService.log({ error, showAlert: true });
    } finally {
      commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
        isUpdating: false,
      });
    }
  },

  async [CUSTOMER_LEADS__STILL_LOOKING](
    { commit, rootState },
    { customerLeadId, propertyId, onSuccess = _.noop }
  ) {
    commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    try {
      await lsApiService.request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${propertyId}/customer-leads/${customerLeadId}/still-looking`,
      });

      onSuccess();
    } catch (error) {
      lsErrorService.log({ error, showAlert: true });
    } finally {
      commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
        isUpdating: false,
      });
    }
  },
  async [CUSTOMER_LEADS__REVIEW_REMIND_ME](
    { dispatch, commit, rootState },
    { customerLeadId, propertyId, onSuccess = _.noop }
  ) {
    commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    try {
      await lsApiService.request({
        method: 'PUT',
        route: `/customers/${customer_id}/properties/${propertyId}/customer-leads/${customerLeadId}/remind-to-review`,
      });

      await dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, { forceRefresh: true });

      onSuccess();
    } catch (error) {
      lsErrorService.log({ error, showAlert: true });
    } finally {
      commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
        isUpdating: false,
      });
    }
  },
  async [CUSTOMER_LEADS__SUBMIT_REVIEW](
    { dispatch, commit, rootState },
    { customerLeadId, propertyId, rating, notes, onSuccess = _.noop }
  ) {
    commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    try {
      await lsApiService.request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${propertyId}/customer-leads/${customerLeadId}/review`,
        payload: {
          rating,
          notes,
        },
      });

      await dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, { forceRefresh: true });

      onSuccess();
    } catch (error) {
      lsErrorService.log({ error, showAlert: true });
    } finally {
      commit(CUSTOMER_LEADS__MUTATE_LOADING_STATUS, {
        isUpdating: false,
      });
    }
  },
};

export const getters = {};

export const customerLeads = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
